import type { Ref } from "vue"
import type { TextToSpeechOptions, TextToSpeechResponse } from "~/types/textToSpeech"
import { useQuery } from "@tanstack/vue-query"

const MAX_RETRIES = 20
const RETRY_INTERVAL = 3_000

export function useTextToSpeech(options: Ref<TextToSpeechOptions | undefined>) {
  const { isAuthorized } = useAuth()
  const enabled = computed(() => !!options.value?.key && isAuthorized.value)

  const api = useApiClients()

  const { data, isFetching } = useQuery({
    queryKey: ["textToSpeech", computed(() => options.value?.key)],
    async queryFn() {
      try {
        return await api.brikke<TextToSpeechResponse>("/v1/tts", {
          method: "POST",
          body: options.value,
        })
      } catch (error) {
        captureException(error)
        throw error
      }
    },
    enabled,
    retry: failureCount => failureCount < MAX_RETRIES,
    refetchInterval: query => query.state.data?.status !== "processed" ? RETRY_INTERVAL : false,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
  })

  return computed(() => {
    if (!enabled.value) {
      return { status: "disabled" } as const
    }

    if (isFetching.value || !data.value) {
      return { status: "loading" } as const
    }

    const audio = data.value.audio?.find(audio => audio.content_type === "audio/mpeg")
    if (!audio && data.value.status !== "processed") {
      return { status: "loading" } as const
    }

    return {
      status: "ready",
      src: audio?.url,
    } as const
  })
}
